import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../api';
import type { InventoryLocation } from '@kontor/shared';

export const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

export const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
export const dateTimeFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
export const formatDay = (iso: string | null) => (iso ? dayFormat.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : '–');

/** „2,5“ → 2500 (Tausendstel), null bei ungültiger Eingabe, leer ergibt null */
export function parseMilli(text: string): number | null {
  let t = text.trim().replace(/\s/g, '');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  if (!/^-?\d*(\.\d*)?$/.test(t) || t === '-' || t === '.') return null;
  const n = Math.round(Number(t) * 1000);
  return Number.isFinite(n) ? n : null;
}

/** Menge als Eingabetext: 2500 → „2,5“ */
export const milliText = (milli: number): string => (milli / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3, useGrouping: false });

/** Alle Lagerorte, einmal geladen und von allen Seiten geteilt */
export function useLocations() {
  return useQuery({ queryKey: ['inventory-locations'], queryFn: async () => (await api<{ data: InventoryLocation[] }>('GET', '/inventory/locations')).data, staleTime: 30_000 });
}

/** Auswahl eines Lagerorts mit vollem Pfad. Deaktivierte Orte erscheinen nur, wenn sie schon gewählt sind. */
export function LocationSelect({ value, onChange, label, allowEmpty, emptyLabel = 'Kein Ort', required }: { value: string; onChange: (id: string) => void; label: string; allowEmpty?: boolean; emptyLabel?: string; required?: boolean }) {
  const locations = useLocations();
  const list = (locations.data ?? []).filter((l) => l.active || l.id === value);
  return (
    <label className="field">
      <span>{label}</span>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)} required={required}>
        {(allowEmpty || !value) && <option value="">{allowEmpty ? emptyLabel : 'Bitte wählen'}</option>}
        {list.map((l) => <option key={l.id} value={l.id}>{l.path}{l.active ? '' : ' (deaktiviert)'}</option>)}
      </select>
    </label>
  );
}

/** Reiter über allen Seiten des Moduls. „Lager“ und „Inventar“ stehen in der Seitenleiste, die Unterseiten hier. */
export function InventoryTabs() {
  return (
    <nav className="subnav" aria-label="Lager">
      <NavLink to="/lager" end>Bestände</NavLink>
      <NavLink to="/lager/bewegungen">Bewegungen</NavLink>
      <NavLink to="/lager/inventur">Inventur</NavLink>
      <NavLink to="/lager/orte">Lagerorte</NavLink>
    </nav>
  );
}

export const statusClass: Record<string, string> = { ok: 'inv-badge--ok', low: 'inv-badge--warn', empty: 'inv-badge--bad' };
